import { useRef } from "react";
import { motion, useScroll, useSpring, useReducedMotion } from "framer-motion";

import { Section, SectionHead } from "../components/ui";
import { PROCESS } from "../data/capabilities";

/**
 * How we work.
 *
 * The rail fills as the section scrolls past, which is the one place on
 * the site where scroll-linked motion earns its cost: the content is
 * literally a sequence, so tying progress to reading position tells the
 * reader where they are in it. Everywhere else, scroll-linked animation
 * would just be parallax with better manners.
 */
export function Process() {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 75%", "end 60%"],
  });
  const fill = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <Section id="how-we-work">
      <div className="container">
        <SectionHead
          eyebrow="How we work"
          title={
            <>
              Five stages.{" "}
              <span className="text-gradient">No mystery in any of them.</span>
            </>
          }
          lede="You will always know which stage we are in, what comes out of it, and what it costs. Engagements go wrong in the gaps between stages, not inside them."
        />

        <div ref={ref} className="relative mt-10 sm:mt-14">
          {/* Rail */}
          <div className="absolute left-[11px] top-2 h-[calc(100%-1rem)] w-px bg-white/10 md:left-1/2 md:-translate-x-1/2" />
          <motion.div
            style={{ scaleY: reduce ? 1 : fill, transformOrigin: "top" }}
            className="absolute left-[11px] top-2 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-emerald-400 via-teal-400 to-violet-500 md:left-1/2 md:-translate-x-1/2"
          />

          <ol className="space-y-8 md:space-y-0">
            {PROCESS.map((p, i) => {
              const right = i % 2 === 1;
              return (
                <motion.li
                  key={p.n}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-70px" }}
                  transition={{ duration: 0.45 }}
                  className="relative pl-9 md:grid md:grid-cols-2 md:gap-12 md:pl-0"
                >
                  {/* Node */}
                  <span className="absolute left-0 top-1.5 grid size-[23px] place-items-center rounded-full border border-white/15 bg-ink-950 md:left-1/2 md:-translate-x-1/2">
                    <span className="size-2 rounded-full bg-gradient-to-br from-emerald-400 to-violet-500" />
                  </span>

                  <div
                    className={`md:py-8 ${
                      right ? "md:col-start-2 md:pl-4" : "md:col-start-1 md:pr-4 md:text-right"
                    }`}
                  >
                    <div className="font-mono text-xs tabular-nums text-white/30">
                      {p.n}
                    </div>
                    <h3 className="mt-1.5 font-display text-lg font-semibold tracking-tight sm:text-xl">
                      {p.title}
                    </h3>
                    <p
                      className={`mt-2 max-w-md text-sm leading-relaxed text-white/60 ${
                        right ? "" : "md:ml-auto"
                      }`}
                    >
                      {p.body}
                    </p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </Section>
  );
}
